import { HeadObjectCommand, ListObjectsV2Command, S3Client, type _Object } from '@aws-sdk/client-s3';
import { awsCredentialsService } from '@/services/awsCredentialsService';
import { clientAccess } from '@/services/clientAccess';
import { getConfigValue } from '@/services/configService';
import { portalAdmin } from '@/services/portalAdmin';
import { mimeTypeOf, uploadToSignedUrl } from '@/services/signedUpload';
import { withPRM } from '@/utils/prmUtils';

export interface MasterSupportDoc {
  key: string;
  size: number;
  lastModified?: string;
  uploadedBy?: string;
}

export interface DeployResult {
  uploadedCount: number;
  cleanedCount: number;
  kbCreated: boolean;
}

function getSupportDocsBucket(): string {
  const bucket = getConfigValue('SUPPORT_DOCS_BUCKET');
  if (!bucket) {
    throw new Error('SUPPORT_DOCS_BUCKET is not configured in portal config');
  }
  return bucket;
}

function toMasterDocs(items: _Object[] | undefined): MasterSupportDoc[] {
  return (items || [])
    .filter((item) => !!item.Key && !item.Key.endsWith('/') && !item.Key.endsWith('.metadata.json'))
    .map((item) => ({
      key: item.Key as string,
      size: item.Size || 0,
      lastModified: item.LastModified?.toISOString(),
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

export class SupportDocsService {
  async listMasterDocs(): Promise<MasterSupportDoc[]> {
    const bucket = getSupportDocsBucket();
    const deployerConfig = await awsCredentialsService.getDeployerClientConfig();
    const s3 = awsCredentialsService.withRevokedRetry(withPRM(S3Client, deployerConfig));
    const response = await s3.send(new ListObjectsV2Command({ Bucket: bucket }));
    const docs = toMasterDocs(response.Contents);

    // Fetch uploaded_by metadata for each doc via HeadObject.
    // Done in parallel for speed; failures are non-fatal.
    await Promise.all(
      docs.map(async (doc) => {
        try {
          const head = await s3.send(new HeadObjectCommand({ Bucket: bucket, Key: doc.key }));
          doc.uploadedBy = head.Metadata?.['uploaded_by'];
        } catch {
          // Non-fatal — just won't show uploader for this file
        }
      })
    );

    return docs;
  }

  /**
   * Uploads one guide to the master set. The portal-admin Lambda checks the
   * name and signs an upload for it; it records the person signed in as the
   * uploader (TASK-630).
   */
  async uploadMasterDoc(file: File): Promise<void> {
    const { url, headers } = await portalAdmin<{ url: string; headers: Record<string, string> }>(
      'supportDocs.uploadUrl',
      { fileName: file.name, contentType: mimeTypeOf(file) }
    );
    await uploadToSignedUrl(url, headers, file);
  }

  async deleteMasterDoc(key: string): Promise<void> {
    await portalAdmin('supportDocs.delete', { key });
  }

  /**
   * Makes the client's Numa Support folder hold the master guides and nothing else. The client
   * access Lambda reads the master set itself and copies it into the client's data bucket, so the
   * guides never pass through the browser. It also makes the folder's knowledge base row if it is
   * missing.
   */
  async deployToClient(clientName: string): Promise<DeployResult> {
    return clientAccess<DeployResult>('supportDocs.deploy', { clientName });
  }
}

export const supportDocsService = new SupportDocsService();
